import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerModal from "../../components/Modal/CustomerModal";
import ContactTable from "../../components/Table/TableContract";
import ContactModal from "../../components/Modal/ContactModal";
import ServiceLocationTable from "../../components/Table/TableLocation";
import ServiceLocationModal from "../../components/Modal/LocationModal";
import CustomerTabs from "../../components/CustomerTabs";
import moment from "moment";
import "moment/locale/th";
import CallTable from "../../components/Table/CallTable";
import CallModal from "../../components/Modal/CallModal";
import AppointmentTable from "../../components/Table/AppointmentTable";
import AppointmentModal from "../../components/Modal/AppointmentModal";
import { getData } from "../../../utils/SamuiUtils";
import { hasOrgPermissionKey } from "../../../utils/orgAccessPermissions";

moment.locale("th");

export default function EditCustomerPage({ embedded = false, id: propId }) {
  const params = useParams();
  const id = propId || params.id;

  const canCustomerUpdate = hasOrgPermissionKey("page.sale_customer.update");
  const canCustomerCreate = hasOrgPermissionKey("page.sale_customer.create");
  const canCustomerDelete = hasOrgPermissionKey("page.sale_customer.delete");
  const canCalendarCreate = hasOrgPermissionKey("page.sale_calendar.create");
  const canCalendarUpdate = hasOrgPermissionKey("page.sale_calendar.update");
  const canCalendarDelete = hasOrgPermissionKey("page.sale_calendar.delete");
  const [customer, setCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [services, setServices] = useState([]);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [calls, setCalls] = useState([]);
  const [titles, setTitles] = useState([]);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [editingCall, setEditingCall] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentData, setAppointmentData] = useState(null);
  const [editingServiceAddrId, setEditingServiceAddrId] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [servicePurposes, setServicePurposes] = useState([]);

  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const res = await api.get("/get-title");
        if (res.data.status === "OK") {
          setTitles(res.data.result || []);
        }
      } catch (err) {
        console.error("โหลดคำนำหน้าไม่สำเร็จ:", err);
        Swal.fire("ผิดพลาด", "ไม่สามารถโหลดคำนำหน้าชื่อได้", "error");
      }
    };
    fetchTitles();
  }, []);

  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const res = await getData(
          "Tb_Set_ServiceAddrType",
          "ServiceAddr_Type_Status = 'Y'"
        );
        const rows = Array.isArray(res) ? res : res?.data || [];
        const mapped = rows
          .map((item) => ({
            id: item?.ServiceAddr_Type_Id ?? item?.id ?? item?.Id,
            name:
              item?.ServiceAddr_Type_Name ??
              item?.name ??
              item?.ServiceAddr_Type,
          }))
          .filter((item) => item.id && item.name);
        setServiceTypes(mapped);
      } catch (err) {
        console.error("โหลดประเภทสถานที่บริการไม่สำเร็จ:", err);
        setServiceTypes([]);
      }
    };
    fetchServiceTypes();
  }, []);

  useEffect(() => {
    const fetchServicePurposes = async () => {
      try {
        const res = await getData(
          "Tb_Set_ServicePurpose",
          "ServicePurpose_Status = 'Y'"
        );
        const rows = Array.isArray(res) ? res : res?.data || [];
        const mapped = rows
          .map((item) => ({
            id:
              item?.ServicePurpose_Id ??
              item?.ServicePurposeId ??
              item?.Purpose_Id ??
              item?.id ??
              item?.Id,
            name:
              item?.ServicePurpose_Name ??
              item?.Purpose_Name ??
              item?.name ??
              item?.ServicePurpose,
          }))
          .filter((item) => item.id && item.name);
        setServicePurposes(mapped);
      } catch (err) {
        console.error("โหลดวัตถุประสงค์ไม่สำเร็จ:", err);
        setServicePurposes([]);
      }
    };
    fetchServicePurposes();
  }, []);

  const fetchCustomer = async () => {
    const res = await api.get(`/get-tb-set-ar1/${id}`);
    const raw = res.data?.result?.recordset?.[0];
    if (raw) {
      raw.Contract_EndDate_Thai = raw.Contract_EndDate
        ? new Date(raw.Contract_EndDate).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        : "-";
    }
    setCustomer(raw || null);
  };

  const fetchContacts = async () => {
    const contactRes = await api.get(`/get-tb-set-ar-contact/${id}`);
    let data = contactRes.data?.result?.recordset || [];
    if (customer?.AR_Group === 1) {
      data = data.map((c) => ({
        ...c,
        AR_ContactName:
          c.AR_ContactName?.trim() ||
          `${c.AR_Name || ""} ${c.AR_LastName || ""}`.trim(),
      }));
    }
    setContacts(data);
  };

  const fetchServices = async () => {
    const serviceRes = await api.get(`/get-tb-set-service-addr/${id}`);
    const serviceList = serviceRes.data?.result?.recordset || [];

    const [callRes, appointRes] = await Promise.all([
      api.get(`/get-calls-list?AR_Id=${id}`),
      api.get(`/get-appoints/${id}`),
    ]);

    const allCalls = callRes.data?.result?.recordset || callRes.data?.result || [];
    const allAppointments =
      appointRes.data?.result?.recordset || appointRes.data?.result || [];

    setCalls(allCalls);
    setAppointments(allAppointments); //  เพิ่มตรงนี้

    const enriched = serviceList.map((s) => {
      const calls = allCalls.filter(
        (c) => Number(c.ServiceAddr_Id) === Number(s.ServiceAddr_Id)
      );

      const apps = allAppointments.filter(
        (a) => Number(a.ServiceAddr_Id) === Number(s.ServiceAddr_Id)
      );

      return { ...s, calls, appointments: apps };
    });

    setServices(enriched);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchCustomer();
        await fetchContacts();
        await fetchServices();
      } catch (error) {
        console.error("โหลดข้อมูลไม่สำเร็จ:", error);
      }
    };
    fetchData();
  }, [id, customer?.AR_Group]);

  const handleSaveCustomer = async (type, formData) => {
    if (!canCustomerUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "บัญชีนี้ไม่มีสิทธิ์แก้ไขข้อมูลลูกค้า", "warning");
      return;
    }
    try {
      const payload = {
        ...customer,
        ...formData,
        AR_Group: type === "corporate" ? 2 : 1,
      };
      await api.put(`/update-tb-set-ar1/${id}`, payload);
      // Swal.fire("สำเร็จ", "อัปเดตข้อมูลลูกค้าสำเร็จ", "success");
      setIsModalOpen(false);
      await fetchCustomer();
    } catch {
      Swal.fire("ผิดพลาด", "อัปเดตไม่สำเร็จ", "error");
    }
  };

  const handleSaveContact = async (form) => {
    if (editingContact && !canCustomerUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขผู้ติดต่อ", "warning");
      return;
    }
    if (!editingContact && !canCustomerCreate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มผู้ติดต่อ", "warning");
      return;
    }
    try {
      if (editingContact) {
        await api.put(
          `/update-service-contact/${editingContact.AR_ContactId}`,
          { ...form, Update_By_Name: "System", Update_By_Id: 0 }
        );
      } else {
        await api.post("/create-service-contact", {
          ...form,
          AR_Id: id,
          Comp_Id: customer.Comp_Id,
          Update_By_Name: "System",
          Update_By_Id: 0,
        });
      }
      await fetchContacts();
      setIsContactModalOpen(false);
      setEditingContact(null);
      // Swal.fire("สำเร็จ", "บันทึกผู้ติดต่อสำเร็จ", "success");
    } catch {
      Swal.fire("ผิดพลาด", "บันทึกผู้ติดต่อไม่สำเร็จ", "error");
    }
  };

  const reloadAll = async () => {
    await Promise.all([
      fetchCustomer(),
      fetchContacts(),
      fetchServices()
    ]);
  };


  const handleSaveService = async (form) => {
    if (editingService && !canCustomerUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขสถานที่บริการ", "warning");
      return { status: "FAILED", error: "no permission" };
    }
    if (!editingService && !canCustomerCreate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มสถานที่บริการ", "warning");
      return { status: "FAILED", error: "no permission" };
    }
    try {
      let serviceId = null;

      //  Insert หรือ Update
      if (editingService) {
        await api.put(
          `/update-tb-set-service-addr/${editingService.ServiceAddr_Id}`,
          {
            ...editingService,
            ...form,
            AR_Id: id,
            Comp_Id: customer.Comp_Id,
            Update_By_Name: "System",
            Update_By_Id: 0,
          }
        );
        serviceId = editingService.ServiceAddr_Id;
      } else {
        const result = await api.post("/create-tb-set-service-addr", {
          ...form,
          AR_Id: id,
          Comp_Id: customer.Comp_Id,
          Update_By_Name: "System",
          Update_By_Id: 0,
        });

        serviceId =
          result.data?.result?.recordset?.[0]?.ServiceAddr_Id ||
          result.data?.ServiceAddr_Id ||
          result.data?.insertId ||
          null;
      }

      //  อัปโหลดไฟล์ถ้ามี
      // if (form.attachedFiles?.length > 0 && serviceId) {
      //   const uploadForm = new FormData();
      //   uploadForm.append("ServiceAddr_Id", serviceId);
      //   uploadForm.append("Comp_Id", customer.Comp_Id);

      //   form.attachedFiles.forEach((item) => {
      //     const realFile = item.file instanceof File ? item.file : item;
      //     uploadForm.append("files", realFile);
      //   });

      //   await api.post(`/upload-service-file/${serviceId}`, uploadForm, {
      //     headers: { "Content-Type": "multipart/form-data" },
      //   });
      // }

      //  ปิด modal ก่อน
      setIsServiceModalOpen(false);
      setEditingService(null);

      //  ชัวร์! โหลดใหม่ทั้งหน้า AFTER upload เสร็จ
      await reloadAll();

      return { status: "OK", ServiceAddr_Id: serviceId };

    } catch (err) {
      console.error("❌ Error:", err);
      Swal.fire("ผิดพลาด", "บันทึกสถานที่บริการไม่สำเร็จ", "error");
      return { status: "FAILED", error: err.message };
    }
  };




  const handleDeleteContact = async (contact) => {
    const confirm = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบผู้ติดต่อ ${contact.AR_ContactName || "-"} ใช่หรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!confirm.isConfirmed) return;

    if (!canCustomerDelete) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์ลบผู้ติดต่อ", "warning");
      return;
    }

    try {
      await api.delete(`/delete-service-contact/${contact.AR_ContactId}`);
      // Swal.fire({
      //   icon: "success",
      //   title: "สำเร็จ!",
      //   text: "ลบข้อมูลผู้ติดต่อแล้ว",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

      await fetchContacts();
    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด!", "ไม่สามารถลบข้อมูลผู้ติดต่อได้", "error");
    }
  };

  const handleDeleteService = async (service) => {
    const confirm = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบสถานที่บริการนี้หรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!confirm.isConfirmed) return;

    if (!canCustomerDelete) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์ลบสถานที่บริการ", "warning");
      return;
    }

    try {
      await api.delete(`/delete-tb-set-service-addr/${service.ServiceAddr_Id}`);
      // Swal.fire({
      //   icon: "success",
      //   title: "สำเร็จ!",
      //   text: "ลบข้อมูลสถานที่บริการแล้ว",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

      await fetchServices();
    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด!", "ไม่สามารถลบข้อมูลสถานที่บริการได้", "error");
    }
  };

  const handleSaveCall = async (mode, payload, callId) => {
    if (mode === "edit" && callId && !canCalendarUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขข้อมูลการโทร/นัดหมาย", "warning");
      return;
    }
    if ((mode !== "edit" || !callId) && !canCalendarCreate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์บันทึกการโทร/นัดหมาย", "warning");
      return;
    }
    try {
      const empId = Number(localStorage.getItem("emp_id")) || 0;     // พนักงานผู้บันทึก
      const empName = localStorage.getItem("name") || "System";       // ชื่อพนักงาน

      const data = {
        ...payload,
        AR_Id: id,
        Comp_Id: customer.Comp_Id,

        Sale_Emp_Id: empId,           //  พนักงานขาย
        Update_By_Id: empId,          //  คนที่อัปเดต = คนเดียวกับ Sale
        Update_By_Name: empName,      //  ชื่อผู้แก้ไข
      };

      if (mode === "edit" && callId) {
        await api.put(`/update-call/${callId}`, data);
      } else {
        await api.post(`/create-call`, data);
      }

      // ปิด modal และ reset state
      setIsCallModalOpen(false);
      setEditingCall(null);

      // โหลดข้อมูลใหม่
      await fetchServices();
      const r = await api.get(`/get-calls-list?AR_Id=${id}`);
      const all = r.data?.result?.recordset || r.data?.result || [];
      setCalls(all);

    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด", "บันทึกข้อมูลการโทรไม่สำเร็จ", "error");
    }
  };




  //  ลบการโทร
  const handleDeleteCall = async (call) => {
    const confirm = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบข้อมูลการโทรนี้หรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });
    if (!confirm.isConfirmed) return;

    if (!canCalendarDelete) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์ลบข้อมูลการโทร", "warning");
      return;
    }

    try {
      await api.delete(`/delete-call/${call.Call_Id}`);

      await fetchServices();
      const r = await api.get(`/get-calls-list?AR_Id=${id}`);
      const all = r.data?.result?.recordset || r.data?.result || [];
      setCalls(all);

      // Swal.fire({
      //   icon: "success",
      //   title: "สำเร็จ!",
      //   text: "ลบข้อมูลการโทรแล้ว",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด!", "ไม่สามารถลบข้อมูลการโทรได้", "error");
    }
  };

  const handleAddAppointment = () => {
    if (!canCalendarCreate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์สร้างนัดหมาย", "warning");
      return;
    }
    const empId = localStorage.getItem("emp_id");
    const empName = localStorage.getItem("name");

    const defaultAddr =
      services[0]?.ServiceAddr_Id ||
      customer?.ServiceAddr_Id ||
      null;

    setAppointmentData({
      AR_Id: id,
      Comp_Id: customer?.Comp_Id,
      ServiceAddr_Id: defaultAddr,
      Appoint_Id: null,
      Purpose: "visit",
      forcePurpose: "visit",
      Appoint_Date: new Date().toISOString().split("T")[0],
      Time_Start: "",
      Time_End: "",
      Appoint_Remark: "",
      Width: "",
      Length: "",
      Height: "",
      SpecRemark: "",
      Sale_Emp_Id: empId,
      Sale_Emp_Name: empName,
      Sale_Latitude: null,
      Sale_Longitude: null,
    });

    setIsAppointmentModalOpen(true);
  };

  const handleEditAppointment = (item) => {
    if (!canCalendarUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขนัดหมาย", "warning");
      return;
    }
    setAppointmentData({
      ...item,
      mode: "edit",
      AR_Id: id,
      Comp_Id: customer.Comp_Id,
      ServiceAddr_Id: item.ServiceAddr_Id,
      Width: item.Width || "",
      Length: item.Length || "",
      Height: item.Height || "",
      SpecRemark: item.SpecRemark || "",
    });

    setIsAppointmentModalOpen(true);
  };

  const handleDeleteAppointment = async (item) => {
    if (!canCalendarDelete) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์ลบนัดหมาย", "warning");
      return;
    }
    try {
      await api.delete(`/delete-appoint/${item.Appoint_Id}`);
      // Swal.fire("สำเร็จ", "ลบข้อมูลการนัดหมายแล้ว", "success");
      fetchServices();
    } catch (error) {
      Swal.fire("ผิดพลาด", "ไม่สามารถลบข้อมูลได้", "error");
    }
  };

  const handleSaveAppointment = async (mode, formData) => {
    if (formData.Appoint_Id && !canCalendarUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขนัดหมาย", "warning");
      return;
    }
    if (!formData.Appoint_Id && !canCalendarCreate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์สร้างนัดหมาย/การโทร", "warning");
      return;
    }
    try {
      const empId = Number(localStorage.getItem("emp_id")) || 0;
      const empName = localStorage.getItem("name") || "System";
      const saleEmpId = Number(formData.Sale_Emp_Id) || empId;

      const isFollowup = formData.Purpose === "followup";
      const endpoint = isFollowup ? "/create-call" : "/create-appoint";

      const base = {
        Comp_Id: formData.Comp_Id,
        AR_Id: id,
        ServiceAddr_Id: formData.ServiceAddr_Id,

        Sale_Emp_Id: saleEmpId,    //  พนักงานขายที่เลือกในนัดหมาย
        Update_By_Id: empId,       //  คนที่แก้ไขล่าสุด
        Update_By_Name: empName,   //  ชื่อผู้แก้ไข
      };

      const payload = isFollowup
        ? {
            ...base,
            Call_Date: formData.Appoint_Date,
            Time_Start: formData.Time_Start,
            Time_End: formData.Time_End,
            Call_Remark: formData.Remark?.trim() || formData.Appoint_Remark || "",
            Call_Type: 2,
          }
        : {
            ...base,
            Purpose: "visit",
            Appoint_Date: formData.Appoint_Date,
            Time_Start: formData.Time_Start,
            Time_End: formData.Time_End,
            Appoint_Remark: formData.Remark?.trim() || "",
            Width: formData.Width ? Number(formData.Width) : null,
            Length: formData.Length ? Number(formData.Length) : null,
            Height: formData.Height ? Number(formData.Height) : null,
            SpecRemark: formData.SpecRemark?.trim() || "",
            Latitude: formData.Latitude || null,
            Longitude: formData.Longitude || null,
          };

      if (formData.Appoint_Id) {
        await api.put(`/update-appoint/${formData.Appoint_Id}`, payload);
      } else {
        await api.post(endpoint, payload);
      }

      setIsAppointmentModalOpen(false);
      fetchServices();
    } catch (error) {
      Swal.fire("ผิดพลาด", "บันทึกข้อมูลไม่สำเร็จ", "error");
    }
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        window.location.href = "/sale";
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const openCustomerEditModal = () => {
    if (!canCustomerUpdate) {
      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขข้อมูลลูกค้า", "warning");
      return;
    }
    setIsModalOpen(true);
  };

  if (!customer) return <p className="p-6">กำลังโหลดข้อมูล...</p>;
  if (embedded) {
    return (
      <div className="w-full h-full bg-gray-50 overflow-auto">

        <main className="w-full min-h-full bg-gray-50 overflow-auto p-8 space-y-8">

          <div className="grid grid-cols-12 gap-6">

            {/*  การ์ดข้อมูลลูกค้า/บริษัท */}
            <div className="col-span-12 md:col-span-4">
              <div className="bg-white p-6 flex flex-col">

                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold h5">ข้อมูลลูกค้า/บริษัท</h3>
                  <button
                    type="button"
                    className="text-blue-500 flex items-center gap-1 disabled:opacity-40"
                    onClick={openCustomerEditModal}
                    disabled={!canCustomerUpdate}
                  >
                    <span className="text-[14px] font-bold underline text-[#2A2A2A]">
                      แก้ไขข้อมูล
                    </span>
                  </button>
                </div>

                <div className="space-y-1">

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700 whitespace-nowrap">ชื่อลูกค้า/บริษัท</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">
                      {(() => {
                        const titleObj = titles.find(
                          (t) => Number(t.Title_Id) === Number(customer.AR_TitleCode)
                        );
                        const title = titleObj ? titleObj.Title_Name : "";
                        const name = customer.AR_Name || "";
                        const lastName = customer.AR_LastName || "";
                        const full = `${title ? title + " " : ""}${name}${lastName ? " " + lastName : ""}`;
                        return full.trim() || "-";
                      })()}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700 whitespace-nowrap">ชื่อที่ใช้ในสัญญา</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">{customer.DisplayName || "-"}</span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700">ประเภทลูกค้า</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">
                      {customer.AR_Group === 1 ? "บุคคลธรรมดา" : "นิติบุคคล"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700">เลขผู้เสียภาษี</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">
                      {customer.AR_TaxNo
                        ? customer.AR_TaxNo.replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/, "$1-$2-$3-$4-$5")
                        : "-"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700">วันที่หมดสัญญา</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">
                      {customer.Contract_EndDate
                        ? moment(customer.Contract_EndDate).add(543, "year").format("MMMM YY")
                        : "-"}
                    </span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700">ชื่อคู่แข่ง</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">{customer.Competitor || "-"}</span>
                  </div>

                  <div className="flex">
                    <span className="w-40 font-bold text-gray-700">ราคาคู่แข่ง</span>
                    <span className="mr-4 font-bold text-gray-700">:</span>
                    <span className="text-gray-800">
                      {customer.CompetitorPrice && !isNaN(customer.CompetitorPrice)
                        ? parseFloat(customer.CompetitorPrice).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        : "-"}
                    </span>
                  </div>

                </div>

              </div>
            </div>

            {/*  การ์ดข้อมูลผู้ติดต่อ */}
            <div className="col-span-12 md:col-span-8">
              <ContactTable
                contacts={contacts}
                customerType={customer.AR_Group === 1 ? "personal" : "corporate"}
                onAdd={() => {
                  if (!canCustomerCreate) {
                    Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มผู้ติดต่อ", "warning");
                    return;
                  }
                  setEditingContact(null);
                  setIsContactModalOpen(true);
                  setEditingServiceAddrId(null);
                }}
                onEdit={(c, addrId) => {
                  if (!canCustomerUpdate) {
                    Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขผู้ติดต่อ", "warning");
                    return;
                  }
                  setEditingContact(c);
                  setEditingServiceAddrId(addrId);
                  setIsContactModalOpen(true);
                }}
                onDelete={handleDeleteContact}
              />
            </div>
          </div>

          {/* ตารางสถานที่บริการ */}
          <ServiceLocationTable
            locations={services}
            serviceTypes={serviceTypes}
            servicePurposes={servicePurposes}
            onAdd={() => {
              if (!canCustomerCreate) {
                Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มสถานที่บริการ", "warning");
                return;
              }
              setEditingService(null);
              setIsServiceModalOpen(true);
            }}
            onEdit={(s) => {
              if (!canCustomerUpdate) {
                Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขสถานที่บริการ", "warning");
                return;
              }
              setEditingService(s);
              setIsServiceModalOpen(true);
            }}
            onDelete={handleDeleteService}
            onAddAppointment={(service) => {
              if (!canCalendarCreate) {
                Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์สร้างนัดหมาย", "warning");
                return;
              }
              setAppointmentData({
                ...service,
                AR_Id: id,
                Comp_Id: customer.Comp_Id,
                ServiceAddr_Id: service.ServiceAddr_Id,
              });
              setIsAppointmentModalOpen(true);
            }}
          />

          <CallTable
            calls={calls}
            onAdd={() => {
              if (!canCalendarCreate) {
                Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์บันทึกการโทร", "warning");
                return;
              }
              setEditingCall(null);
              setIsCallModalOpen(true);
            }}
            onEdit={(call) => {
              if (!canCalendarUpdate) {
                Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขการโทร", "warning");
                return;
              }
              setEditingCall(call);
              setIsCallModalOpen(true);
            }}
            onDelete={handleDeleteCall}
          />

          <AppointmentTable
            appointments={appointments}
            onAdd={handleAddAppointment}
            onEdit={handleEditAppointment}
            onDelete={handleDeleteAppointment}
          />

        </main>

        {/* Modals */}
        <CustomerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveCustomer}
          customer={customer}
          forceTab={customer.AR_Group === 1 ? "personal" : "corporate"}
        />
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          onSave={handleSaveContact}
          contact={editingContact}
          customerType={customer.AR_Group === 1 ? "personal" : "corporate"}
          ServiceAddr_Id={editingServiceAddrId}
        />
        <ServiceLocationModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          onSave={handleSaveService}
          onReload={fetchServices}
          location={editingService}
          ServiceAddr_Id={editingService?.ServiceAddr_Id}
          serviceTypes={serviceTypes}
          AR_Id={id}
          fromCustomerPage={true}
        />

        <CallModal
          isOpen={isCallModalOpen}
          onClose={() => setIsCallModalOpen(false)}
          onSave={handleSaveCall}
          initialData={editingCall}
          customerData={{
            AR_Id: id,
            Comp_Id: customer.Comp_Id,
            ServiceAddr_Id: null,
          }}
        />

        <AppointmentModal
          isOpen={isAppointmentModalOpen}
          onClose={() => setIsAppointmentModalOpen(false)}
          initialData={appointmentData}
          customerData={customer}
          onSave={handleSaveAppointment}
          fromCustomerPage={true}
        />


      </div>
    );
  } else {
    return (
      <div className="flex">
        {/* ✔ Sidebar */}
        <Sidebar />

        {/* ✔ Main */}
        <main
          className="flex-1 h-screen overflow-auto bg-gray-50 transition-all duration-300 edit-customer-page"
          style={{ marginLeft: "var(--sidebar-width)" }}
        >
          <style>{`
            .edit-customer-page .sticky-action,
            .edit-customer-page .sticky-appointment {
              z-index: 5 !important;
            }
            .edit-customer-page thead th.sticky-action,
            .edit-customer-page thead th.sticky-appointment {
              z-index: 6 !important;
            }
          `}</style>
          <Topbar
            breadcrumbs={[
              { label: "ลูกค้า", link: "" },
              { label: "รายการลูกค้า", link: "/sale" },
              { label: "ข้อมูลลูกค้า" },
            ]}
          />

          <div className="sticky top-14 z-20 bg-white pl-0 pr-6">
            <CustomerTabs customer={customer} className="pl-2 pr-6 py-6" />
          </div>



          {/* ✔ Content */}
          <div className="pl-2 pr-8 space-y-8 pt-[56px] pb-[180px]">

            {/* ==== การ์ดข้อมูลลูกค้า ==== */}
            <div className="grid grid-cols-12 gap-6">

              {/* การ์ดข้อมูลลูกค้า */}
              <div className="col-span-12 md:col-span-4">
                <div className="bg-white p-6 flex flex-col">

                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold h5">ข้อมูลลูกค้า/บริษัท</h3>
                    <button
                      type="button"
                      className="text-blue-500 flex items-center gap-1 disabled:opacity-40"
                      onClick={openCustomerEditModal}
                      disabled={!canCustomerUpdate}
                    >
                      <span className="text-[14px] font-bold underline text-[#2A2A2A]">
                        แก้ไขข้อมูล
                      </span>
                    </button>
                  </div>

                  {/* ข้อมูลลูกค้า */}
                  <div className="space-y-1">

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700 whitespace-nowrap">ชื่อลูกค้า/บริษัท</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">
                        {(() => {
                          const titleObj = titles.find(
                            (t) => Number(t.Title_Id) === Number(customer.AR_TitleCode)
                          );
                          const title = titleObj ? titleObj.Title_Name : "";
                          const name = customer.AR_Name || "";
                          const lastName = customer.AR_LastName || "";
                          const full = `${title ? title + " " : ""}${name}${lastName ? " " + lastName : ""}`;
                          return full.trim() || "-";
                        })()}
                      </span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700 whitespace-nowrap">ชื่อที่ใช้ในสัญญา</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">{customer.DisplayName || "-"}</span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700">ประเภทลูกค้า</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">
                        {customer.AR_Group === 1 ? "บุคคลธรรมดา" : "นิติบุคคล"}
                      </span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700">เลขผู้เสียภาษี</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">
                        {customer.AR_TaxNo
                          ? customer.AR_TaxNo.replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/, "$1-$2-$3-$4-$5")
                          : "-"}
                      </span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700">วันที่หมดสัญญา</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">
                        {customer.Contract_EndDate
                          ? moment(customer.Contract_EndDate).add(543, "year").format("MMMM YY")
                          : "-"}
                      </span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700">ชื่อคู่แข่ง</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">{customer.Competitor || "-"}</span>
                    </div>

                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700">ราคาคู่แข่ง</span>
                      <span className="mr-4 font-bold text-gray-700">:</span>
                      <span className="text-gray-800">
                        {customer.CompetitorPrice && !isNaN(customer.CompetitorPrice)
                          ? parseFloat(customer.CompetitorPrice).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          : "-"}
                      </span>
                    </div>

                  </div>

                </div>
              </div>

              {/* ========== การ์ดผู้ติดต่อ ========== */}
              <div className="col-span-12 md:col-span-8">
                <ContactTable
                  contacts={contacts}
                  customerType={customer.AR_Group === 1 ? "personal" : "corporate"}
                  onAdd={() => {
                    if (!canCustomerCreate) {
                      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มผู้ติดต่อ", "warning");
                      return;
                    }
                    setEditingContact(null);
                    setIsContactModalOpen(true);
                    setEditingServiceAddrId(null);
                  }}
                  onEdit={(c, addrId) => {
                    if (!canCustomerUpdate) {
                      Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขผู้ติดต่อ", "warning");
                      return;
                    }
                    setEditingContact(c);
                    setEditingServiceAddrId(addrId);
                    setIsContactModalOpen(true);
                  }}
                  onDelete={handleDeleteContact}
                />
              </div>

            </div>

            {/* ==== สถานที่บริการ ==== */}
            <ServiceLocationTable
              locations={services}
              serviceTypes={serviceTypes}
              servicePurposes={servicePurposes}
              onAdd={() => {
                if (!canCustomerCreate) {
                  Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์เพิ่มสถานที่บริการ", "warning");
                  return;
                }
                setEditingService(null);
                setIsServiceModalOpen(true);
              }}
              onEdit={(s) => {
                if (!canCustomerUpdate) {
                  Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขสถานที่บริการ", "warning");
                  return;
                }
                setEditingService(s);
                setIsServiceModalOpen(true);
              }}
              onDelete={handleDeleteService}
              onAddAppointment={(service) => {
                if (!canCalendarCreate) {
                  Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์สร้างนัดหมาย", "warning");
                  return;
                }
                setAppointmentData({
                  ...service,
                  AR_Id: id,
                  Comp_Id: customer.Comp_Id,
                  ServiceAddr_Id: service.ServiceAddr_Id,

                  Purpose: "visit",
                  forcePurpose: "visit",
                });
                setIsAppointmentModalOpen(true);
              }}

            />

            {/* ==== Call ==== */}
            <CallTable
              calls={calls}
              onAdd={() => {
                if (!canCalendarCreate) {
                  Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์บันทึกการโทร", "warning");
                  return;
                }
                setEditingCall(null);
                setIsCallModalOpen(true);
              }}
              onEdit={(call) => {
                if (!canCalendarUpdate) {
                  Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์แก้ไขการโทร", "warning");
                  return;
                }
                setEditingCall(call);
                setIsCallModalOpen(true);
              }}
              onDelete={handleDeleteCall}
            />

            {/* ==== Appointments ==== */}
            <AppointmentTable
              appointments={appointments}
              onAdd={handleAddAppointment}
              onEdit={handleEditAppointment}
              onDelete={handleDeleteAppointment}
            />

          </div>

          {/* ==== ปุ่มด้านล่าง ==== */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg"
            style={{ marginLeft: "var(--sidebar-width)", zIndex: 1000 }}
          >
            <div className="flex justify-between items-center px-8 py-4 w-full">
              {/* ปุ่มลบ */}
              <button
                type="button"
                disabled={!canCustomerDelete}
                onClick={async () => {
                  if (!canCustomerDelete) {
                    Swal.fire("ไม่มีสิทธิ์", "ไม่มีสิทธิ์ลบลูกค้า", "warning");
                    return;
                  }
                  const result = await Swal.fire({
                    title: "คุณแน่ใจหรือไม่?",
                    text: `คุณต้องการลบลูกค้า ${customer.AR_Name} ใช่หรือไม่?`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#808080",
                    cancelButtonColor: "#6c757d",
                    confirmButtonText: "ลบ",
                    cancelButtonText: "ยกเลิก",
                  });

                  if (result.isConfirmed) {
                    try {
                      await api.delete(`/delete-tb-set-ar1/${id}`);
                      window.location.href = "/sale";
                    } catch {
                      Swal.fire("ผิดพลาด!", "ไม่สามารถลบข้อมูลลูกค้าได้", "error");
                    }
                  }
                }}
                className="flex w-[200px] h-10 justify-center items-center gap-2 rounded-lg text-white font-bold transition disabled:opacity-40"
                style={{
                  backgroundColor: "#808080",
                  borderRadius: "8px",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#666")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#808080")}
              >
                ลบ
              </button>

              {/* ปุ่มกลับ */}
              <button
                onClick={() => (window.location.href = "/sale")}
                className="flex w-[200px] h-10 justify-center items-center gap-2 rounded-lg text-white font-bold transition"
                style={{
                  backgroundColor: "black",
                  borderRadius: "8px",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#333")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "black")}
              >
                กลับหน้าค้นหา
              </button>
            </div>
          </div>


          {/* ==== Modals ==== */}
          <CustomerModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSaveCustomer}
            customer={customer}
            forceTab={customer.AR_Group === 1 ? "personal" : "corporate"}
          />

          <ContactModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            onSave={handleSaveContact}
            contact={editingContact}
            customerType={customer.AR_Group === 1 ? "personal" : "corporate"}
            ServiceAddr_Id={editingServiceAddrId}
          />

          <ServiceLocationModal
            isOpen={isServiceModalOpen}
            onClose={() => setIsServiceModalOpen(false)}
            onSave={handleSaveService}
            onReload={fetchServices}
            location={editingService}
            ServiceAddr_Id={editingService?.ServiceAddr_Id}
            serviceTypes={serviceTypes}
            AR_Id={id}
            fromCustomerPage={true}
          />

          <CallModal
            isOpen={isCallModalOpen}
            onClose={() => setIsCallModalOpen(false)}
            onSave={handleSaveCall}
            initialData={editingCall}
            customerData={{
              AR_Id: id,
              Comp_Id: customer.Comp_Id,
              ServiceAddr_Id: null,
            }}
          />

          <AppointmentModal
            isOpen={isAppointmentModalOpen}
            onClose={() => setIsAppointmentModalOpen(false)}
            initialData={appointmentData}
            customerData={customer}
            onSave={handleSaveAppointment}
            fromCustomerPage={true}
          />

        </main>
      </div>
    );
  }

}
